/**
 * Wizard State Reducer
 *
 * Handles all state transitions with validation gates.
 * Ensures step completion requirements are met before navigation.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import {
  WizardState,
  WizardAction,
  WizardStep,
  WIZARD_STEPS,
  createInitialState,
} from './types';

// =============================================================================
// NAVIGATION HELPERS
// =============================================================================

/**
 * Get the next step in the wizard sequence.
 */
function getNextStep(current: WizardStep): WizardStep | null {
  const currentIndex = WIZARD_STEPS.indexOf(current);
  if (currentIndex < WIZARD_STEPS.length - 1) {
    const next = WIZARD_STEPS[currentIndex + 1];
    return next ?? null;
  }
  return null;
}

/**
 * Get the previous step in the wizard sequence.
 */
function getPrevStep(current: WizardStep): WizardStep | null {
  const currentIndex = WIZARD_STEPS.indexOf(current);
  if (currentIndex > 0) {
    const prev = WIZARD_STEPS[currentIndex - 1];
    return prev ?? null;
  }
  return null;
}

/**
 * Check if navigation to a target step is allowed.
 * Users can always go back, but can only go forward if previous steps are complete.
 */
function canNavigateToStep(
  state: WizardState,
  targetStep: WizardStep
): boolean {
  const targetIndex = WIZARD_STEPS.indexOf(targetStep);
  const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);

  // Can always go back
  if (targetIndex < currentIndex) {
    return true;
  }

  // Can only go forward if all previous steps are complete
  for (let i = 0; i < targetIndex; i++) {
    const step = WIZARD_STEPS[i];
    if (step && !state.completedSteps.has(step)) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a specific step is complete based on its requirements.
 */
function isStepComplete(state: WizardState, step: WizardStep): boolean {
  switch (step) {
    case 'source':
      // Need source type selected
      if (!state.sourceType) return false;
      // If not manual, need file uploaded and parsed successfully
      if (state.sourceType !== 'manual') {
        return state.parseResult?.success === true;
      }
      return true;

    case 'period':
      // Need valid period (basic validation)
      return (
        state.contributionMonth >= 1 &&
        state.contributionMonth <= 12 &&
        state.contributionYear >= 2020 &&
        state.contributionYear <= 2100
      );

    case 'validate': {
      // Need employees and validation passed (or acknowledged)
      if (state.employees.length === 0) return false;
      if (!state.validationResult) return false;
      // Cannot proceed with errors
      if (state.validationResult.summary.total_errors > 0) return false;
      // All warnings must be acknowledged
      const warningCount = state.validationResult.summary.total_warnings;
      if (warningCount > 0) {
        // Check that enough warnings are acknowledged
        return state.acknowledgedWarnings.size >= warningCount;
      }
      return true;
    }

    case 'generate':
      return state.generatedFile !== null;

    default:
      return false;
  }
}

// =============================================================================
// REDUCER
// =============================================================================

/**
 * Main wizard reducer function.
 */
export function wizardReducer(
  state: WizardState,
  action: WizardAction
): WizardState {
  switch (action.type) {
    // =========================================================================
    // SOURCE ACTIONS
    // =========================================================================

    case 'SET_SOURCE_TYPE':
      return {
        ...state,
        sourceType: action.payload,
        // Reset downstream state when source changes
        uploadedFile: null,
        parseResult: null,
        employees: [],
        validationResult: null,
        acknowledgedWarnings: new Set(),
        generatedFile: null,
        error: null,
      };

    case 'SET_UPLOADED_FILE':
      return {
        ...state,
        uploadedFile: action.payload,
        // Reset parse result when file changes
        parseResult: null,
        employees: [],
        validationResult: null,
        error: null,
      };

    case 'SET_PARSE_RESULT':
      return {
        ...state,
        parseResult: action.payload,
        employees: action.payload.success ? action.payload.employees : [],
        error: action.payload.success
          ? null
          : action.payload.errors[0] || 'Failed to parse file',
      };

    // =========================================================================
    // PERIOD ACTIONS
    // =========================================================================

    case 'SET_PERIOD':
      return {
        ...state,
        contributionMonth: action.payload.month,
        contributionYear: action.payload.year,
        // Clear validation when period changes (affects ceiling calculations)
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };

    case 'SET_FORMAT_TYPE':
      return {
        ...state,
        formatType: action.payload,
        // Clear validation when format changes
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };

    // =========================================================================
    // EMPLOYEE ACTIONS
    // =========================================================================

    case 'SET_EMPLOYEES':
      return {
        ...state,
        employees: action.payload,
        // Clear validation when employees change
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };

    case 'UPDATE_EMPLOYEE': {
      const { id, updates } = action.payload;
      return {
        ...state,
        employees: state.employees.map(emp =>
          emp.id === id ? { ...emp, ...updates } : emp
        ),
        // Clear validation when employee data changes
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };
    }

    case 'ADD_EMPLOYEE':
      return {
        ...state,
        employees: [...state.employees, action.payload],
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };

    case 'REMOVE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.filter(emp => emp.id !== action.payload),
        validationResult: null,
        acknowledgedWarnings: new Set(),
      };

    // =========================================================================
    // VALIDATION ACTIONS
    // =========================================================================

    case 'SET_VALIDATION_RESULT':
      return {
        ...state,
        validationResult: action.payload,
      };

    case 'ACKNOWLEDGE_WARNING':
      return {
        ...state,
        acknowledgedWarnings: new Set(state.acknowledgedWarnings).add(
          action.payload
        ),
      };

    // =========================================================================
    // GENERATE ACTIONS
    // =========================================================================

    case 'SET_GENERATED_FILE':
      return {
        ...state,
        generatedFile: action.payload,
        completedSteps: new Set(state.completedSteps).add('generate'),
      };

    // =========================================================================
    // NAVIGATION ACTIONS
    // =========================================================================

    case 'GO_TO_STEP':
      if (!canNavigateToStep(state, action.payload)) {
        return state;
      }
      return {
        ...state,
        currentStep: action.payload,
        error: null,
      };

    case 'NEXT_STEP': {
      // Check if current step is complete
      if (!isStepComplete(state, state.currentStep)) {
        return state;
      }

      const nextStep = getNextStep(state.currentStep);
      if (!nextStep) return state;

      return {
        ...state,
        currentStep: nextStep,
        completedSteps: new Set(state.completedSteps).add(state.currentStep),
        error: null,
      };
    }

    case 'PREV_STEP': {
      const prevStep = getPrevStep(state.currentStep);
      if (!prevStep) return state;

      return {
        ...state,
        currentStep: prevStep,
        error: null,
      };
    }

    // =========================================================================
    // UI ACTIONS
    // =========================================================================

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'RESET':
      return createInitialState();

    default:
      return state;
  }
}

// =============================================================================
// EXPORTS FOR TESTING
// =============================================================================

export { isStepComplete, canNavigateToStep, getNextStep, getPrevStep };
